import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useAngularContext } from './context';

/**
 * Returns a boolean indicating whether or not an Angular UI is mounted
 */
export const useIsAngularMounted = () => !!useAngularContext().scope;

/**
 * Allows a React component to bind a callback function to the Angular scope,
 * making it possible for Angular code to invoke React-provided functions.
 */
export const useAngularCallback = <Arguments extends any[] = [], Result = void>(
  keyOnRootScope: string,
  callback: (...args: Arguments) => Result,
) => {
  const { scope } = useAngularContext();
  useEffect(() => {
    if (!scope?.$root) return;

    scope.$root[keyOnRootScope] = callback;

    return () => {
      // $root can be null by the time this is invoked so we optionally check for the [keyOnRootScope] prop
      if (scope.$root?.[keyOnRootScope] === callback) delete scope.$root[keyOnRootScope];
    };
  }, [scope, callback]);
};

/**
 * Allows a React component to react to watched changes in the Angular scope.
 */
export const useAngularEffect = (callback: () => void, watchedRootScopeKeys: string[]) => {
  const { scope } = useAngularContext();
  useEffect(() => {
    callback();
    if (!scope) return;
    return scope.$root.$watchGroup(watchedRootScopeKeys, callback);
  }, [scope, ...watchedRootScopeKeys]);
};

/**
 * Allows for synchronized state between React and Angular in a signature similar to `useState`
 */
export const useAngularState = <Value>(
  keyOnRootScope: string,
  defaultInitialValue: Value,
): [Value, Dispatch<SetStateAction<Value>>] => {
  const { scope } = useAngularContext();
  const [value, setValue] = useState<Value>(scope?.$root[keyOnRootScope] ?? defaultInitialValue);

  // Sync Angular changes to React state
  useAngularEffect(() => {
    setValue(scope?.$root[keyOnRootScope]);
  }, [keyOnRootScope]);

  // Sync React changes to Angular scope
  useEffect(() => {
    if (scope) scope.$root[keyOnRootScope] = value;
  }, [scope, value]);

  return [value, setValue];
};
